import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { TacoAssetType } from "@amzn/afxtaco-types";
import { logger } from "@amzn/afxtaco-utilities";
import { reducerType } from "../../../redux/reducers";
import { isExecutionActive } from "../../../redux/reducers/Execution";
import { useProjectAsset } from "../../hooks/useProjectAsset";
import { saveDiagram } from "../../api/projects/saveDiagram";
import { setFlushFn } from "./drawioFlushRegistry";
import {
  DrawioAction,
  DRAWIO_BLANK_XML,
  DRAWIO_EMBED_URL,
  sendDrawioMessage,
} from "./drawioProtocol";
import { useDrawioMessaging } from "./useDrawioMessaging";

export interface DrawioEditorProps {
  projectId: string;
}

/** Max age of a dirty buffer before forcing a flush even without a
 *  boundary event — bounds worst-case edit-loss on hard shutdown. */
const BACKSTOP_FLUSH_MS = 30_000;

/**
 * Embeds the Amazon-internal drawio editor and roundtrips XML both ways.
 *
 * Agent → iframe: when the BIDI_DIAGRAM asset changes, new XML is pushed via
 * LOAD postMessage (drawio swaps content in place, no remount).
 *
 * Iframe → agent: drawio's `autosave` postMessages fire on every edit. We
 * buffer the latest XML locally and POST to the ingestion endpoint only on
 * boundary events — TACO turn starting on this project, tab hidden,
 * unmount, or a 30s backstop timer. Every POST produces one Version (the
 * server short-circuits byte-identical redundant flushes). Autosaves during
 * an active TACO turn on this project are dropped so user writes don't race
 * TACO's end-of-turn write.
 */
const DrawioEditor: React.FC<DrawioEditorProps> = ({ projectId }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Drawio silently drops postMessages before it fires `init`. Gate LOAD on this.
  const [isReady, setIsReady] = useState(false);

  // Lock embed URL to the page's theme at mount. Drawio reads `ui` from the
  // URL once on load; live toggles are picked up via prefers-color-scheme
  // (next-themes sets color-scheme on html, which propagates to the iframe).
  // Re-mount (e.g. tab return after toggle) re-evaluates this initializer.
  const [embedUrl] = useState(() => {
    const isDark =
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark");
    return isDark
      ? DRAWIO_EMBED_URL.replace("ui=atlas", "ui=dark")
      : DRAWIO_EMBED_URL;
  });

  // useProjectAsset re-fetches when the project's staleCounter bumps (after
  // agent writes), driving the LOAD effect below.
  const { content } = useProjectAsset({
    projectId,
    assetType: TacoAssetType.BIDI_DIAGRAM,
  });

  // Scope TACO-turn detection to this project — a turn on a different
  // project must not pause autosaves here.
  const executionStatus = useSelector(
    (state: reducerType) => state.Execution.status,
  );
  const executionProjectId = useSelector(
    (state: reducerType) => state.Execution.projectId,
  );
  const isTacoTurnActive =
    isExecutionActive(executionStatus) && executionProjectId === projectId;
  const isTacoTurnActiveRef = useRef(isTacoTurnActive);
  const wasTacoTurnActiveRef = useRef(isTacoTurnActive);
  isTacoTurnActiveRef.current = isTacoTurnActive;

  // Buffer state — refs avoid re-render on every edit.
  const bufferRef = useRef<string | null>(null); // latest XML from autosave
  const isDirtyRef = useRef(false); // unflushed content pending
  const lastFlushedXmlRef = useRef<string | null>(null); // last XML sent successfully
  const backstopTimerRef = useRef<number | null>(null); // armed on clean→dirty

  // Pending export request — one outstanding at a time, may be PNG or XML.
  // The format is only used to tell drawio what to render; the response is
  // delivered as a single string to the resolver, which knows what to expect.
  const pendingExportRef = useRef<{
    format: "png" | "xml";
    resolve: (value: string | null) => void;
    timeoutId: number | null;
  } | null>(null);

  /** Send an EXPORT request to the iframe and wait for the response.
   *  Resolves with the response string or null on timeout / not ready.
   *  1.5s budget bounds flush latency cost. */
  const requestExport = useCallback(
    (format: "png" | "xml"): Promise<string | null> => {
      return new Promise((resolve) => {
        const iframe = iframeRef.current;
        if (!iframe || !isReady) {
          resolve(null);
          return;
        }
        // Cancel any prior pending request.
        const prior = pendingExportRef.current;
        if (prior) {
          if (prior.timeoutId !== null) {
            window.clearTimeout(prior.timeoutId);
          }
          prior.resolve(null);
        }
        const entry: {
          format: "png" | "xml";
          resolve: (value: string | null) => void;
          timeoutId: number | null;
        } = { format, resolve, timeoutId: null };
        entry.timeoutId = window.setTimeout(() => {
          if (pendingExportRef.current === entry) {
            pendingExportRef.current = null;
            resolve(null);
          }
        }, 1500);
        pendingExportRef.current = entry;
        // PNG renders go out at width 2400 so text stays readable when the
        // PNG is downscaled to fit a typical viewport.
        const message =
          format === "png"
            ? {
                action: DrawioAction.EXPORT,
                format: "png" as const,
                width: 2400,
              }
            : {
                action: DrawioAction.EXPORT,
                format: "xml" as const,
              };
        sendDrawioMessage(iframe, message);
      });
    },
    [isReady],
  );

  const requestXmlExport = useCallback(
    () => requestExport("xml"),
    [requestExport],
  );

  /** POST the buffer if dirty. No-op if clean or unchanged since last flush.
   *  `reason` is logged for CloudWatch correlation. */
  const flush = useCallback(
    async (reason: string): Promise<void> => {
      const keepalive = reason === "unmount" || reason === "visibility-hidden";

      logger.log({
        loggerMsg: "diagram_diff: flush start",
        reason,
        keepalive,
        isDirty: isDirtyRef.current,
        bufferLen: bufferRef.current?.length ?? 0,
        lastFlushedLen: lastFlushedXmlRef.current?.length ?? 0,
      });

      // Pull fresh XML from drawio synchronously so we capture state that
      // hasn't been autosave-debounced yet. Closes the gap where a user
      // edits and immediately submits chat: bufferRef would otherwise
      // hold the previous autosave, missing the most recent edit.
      // Skipped on keepalive flushes — fetch keepalive doesn't have time
      // for an extra round-trip with the iframe.
      if (!keepalive) {
        const freshXml = await requestXmlExport();
        if (freshXml != null) {
          if (freshXml !== lastFlushedXmlRef.current) {
            bufferRef.current = freshXml;
            isDirtyRef.current = true;
          } else {
            // Canvas matches what we already flushed; nothing to send.
            isDirtyRef.current = false;
          }
        }
      }

      if (!isDirtyRef.current) return;
      const xml = bufferRef.current;
      if (xml == null || xml.length === 0) {
        isDirtyRef.current = false;
        return;
      }
      if (xml === lastFlushedXmlRef.current) {
        // Already synced — dirty flag was a false alarm (e.g. unmount
        // right after a backstop flush).
        isDirtyRef.current = false;
        return;
      }
      // Clear dirty state and cancel the backstop up front so a concurrent
      // flush while this POST is in flight won't double-send. On failure
      // we intentionally do NOT re-mark dirty; the next autosave will
      // re-dirty naturally. Accepts losing a pending edit if the user
      // stops editing after a failed flush.
      isDirtyRef.current = false;
      if (backstopTimerRef.current !== null) {
        window.clearTimeout(backstopTimerRef.current);
        backstopTimerRef.current = null;
      }
      const startMs = Date.now();
      try {
        const result = await saveDiagram({ projectId, xml }, { keepalive });
        lastFlushedXmlRef.current = xml;
        logger.log({
          loggerMsg: "Drawio: flush succeeded",
          reason,
          projectId,
          xmlLength: xml.length,
          outcome: result.outcome,
          elapsedMs: Date.now() - startMs,
        });
      } catch (err) {
        // Re-dirty so a future flush retries this edit. Skip if a newer
        // autosave arrived during the in-flight POST and already dirtied
        // with newer content.
        if (xml === bufferRef.current) {
          isDirtyRef.current = true;
          // Re-arm the backstop so the retry isn't deferred to the next
          // external boundary event. The autosave handler only arms on
          // clean→dirty, so without this the timer would stay cleared.
          if (backstopTimerRef.current === null) {
            backstopTimerRef.current = window.setTimeout(() => {
              backstopTimerRef.current = null;
              void flush("backstop-timer");
            }, BACKSTOP_FLUSH_MS);
          }
        }
        logger.error({
          loggerMsg: "Drawio: flush failed",
          reason,
          projectId,
          xmlLength: xml.length,
          elapsedMs: Date.now() - startMs,
          err,
        });
      }
    },
    [projectId, requestXmlExport],
  );

  // Flush on TACO turn-start (false→true transition). TACO reads stored
  // XML at turn start; flushing here makes sure it sees the user's edits.
  useEffect(() => {
    const was = wasTacoTurnActiveRef.current;
    wasTacoTurnActiveRef.current = isTacoTurnActive;
    if (!was && isTacoTurnActive) void flush("turn-start");
  }, [isTacoTurnActive, flush]);

  // Flush on tab-hidden — catches browser-close paths that skip unmount.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden")
        void flush("visibility-hidden");
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [flush]);

  // Flush on unmount — catches project switch, tab teardown, any other exit.
  // Ref indirection so the cleanup always calls the latest `flush`.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    return () => {
      void flushRef.current("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose flush so chat-submit can await it before pinging the
  // orchestrator, closing the agent-reads-stale-S3 race.
  useEffect(() => {
    setFlushFn(() => flush("chat-submit"));
    return () => setFlushFn(null);
  }, [flush]);

  // Drawio autosave fires on every edit. We buffer — boundary events and
  // the backstop do the POSTing. During a TACO turn we log and drop.
  useDrawioMessaging({
    onInit: useCallback(() => {
      setIsReady(true);
    }, []),
    onAutosave: useCallback(
      (xml: string) => {
        if (isTacoTurnActiveRef.current) {
          logger.log({
            loggerMsg: "Drawio: autosave dropped while TACO turn active",
            projectId,
            xmlLength: xml.length,
          });
          return;
        }
        bufferRef.current = xml;
        const wasDirty = isDirtyRef.current;
        isDirtyRef.current = true;
        if (!wasDirty && backstopTimerRef.current === null) {
          backstopTimerRef.current = window.setTimeout(() => {
            backstopTimerRef.current = null;
            void flush("backstop-timer");
          }, BACKSTOP_FLUSH_MS);
        }
        logger.log({
          loggerMsg: "Drawio: autosave observed",
          projectId,
          xmlLength: xml.length,
        });
      },
      [projectId, flush],
    ),
    onPngExport: useCallback((dataUrl: string) => {
      const pending = pendingExportRef.current;
      if (!pending || pending.format !== "png") return;
      if (pending.timeoutId !== null) {
        window.clearTimeout(pending.timeoutId);
      }
      pendingExportRef.current = null;
      pending.resolve(dataUrl);
    }, []),
    onXmlExport: useCallback((xml: string) => {
      const pending = pendingExportRef.current;
      if (!pending || pending.format !== "xml") return;
      if (pending.timeoutId !== null) {
        window.clearTimeout(pending.timeoutId);
      }
      pendingExportRef.current = null;
      pending.resolve(xml);
    }, []),
  });

  // Push current content to the iframe on ready and on agent-initiated
  // content changes. `autosave: 1` enables drawio's autosave emission.
  // A new LOAD establishes the baseline — reset dirty state so drawio's
  // post-LOAD reserialization autosaves aren't treated as user edits.
  useEffect(() => {
    if (!isReady) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const xml =
      content && content.trim().length > 0 ? content : DRAWIO_BLANK_XML;
    // Skip when content matches what we just flushed — the iframe already
    // shows it, and re-LOADing would wipe any in-flight buffer.
    if (xml !== lastFlushedXmlRef.current) {
      sendDrawioMessage(iframe, {
        action: DrawioAction.LOAD,
        xml,
        autosave: 1,
      });
      bufferRef.current = xml;
      isDirtyRef.current = false;
      lastFlushedXmlRef.current = xml;
      if (backstopTimerRef.current !== null) {
        window.clearTimeout(backstopTimerRef.current);
        backstopTimerRef.current = null;
      }
    }
  }, [content, isReady]);

  return (
    <div className="relative flex h-full flex-col">
      {isTacoTurnActive && (
        <div className="absolute top-2 right-2 z-10 rounded-md bg-muted/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur">
          TACO is working — your edits will not save until it finishes.
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title="Architecture diagram"
        className="flex-1 border-0 bg-background"
      />
    </div>
  );
};

export default DrawioEditor;
